import dayjs from 'dayjs';
import type { DeliveryMetricsResponse } from '../types/deliveryMetrics';

/**
 * CSV / PDF export for the delivery-metrics dashboard.
 *
 * The CSV serialization is hand-rolled and kept as pure functions so it can
 * be unit-tested. The PDF builder lazy-loads jspdf so the library stays out
 * of the main bundle until an export is actually requested.
 */

/** Filter context echoed into the export header so a downloaded file is
 * self-describing about what was on screen when it was taken. */
export interface DeliveryMetricsExportFilters {
  /** Relative window in minutes — omitted when an explicit date range is active. */
  timeWindowMinutes?: number;
  /** Explicit range start, YYYY-MM-DD (overrides the time window). */
  startDate?: string;
  /** Explicit range end, YYYY-MM-DD. */
  endDate?: string;
  /** Resolved site name; omitted = all sites. */
  siteName?: string;
  /** Notification category label; omitted = all categories. */
  categoryLabel?: string;
  /** Resolved AlertType ("Protocol") name; omitted = all protocols. */
  protocolLabel?: string;
}

/**
 * Escape a single CSV field per RFC 4180: wrap in double quotes when the
 * value contains a comma, double quote, CR/LF, or leading/trailing
 * whitespace; embedded quotes are doubled. Null/undefined serialize to an
 * empty field.
 */
export function csvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  const str = String(value);
  if (/[",\r\n]/.test(str) || str !== str.trim()) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Serialize one CSV row (no terminator — joining is the document's job). */
export function csvRow(cells: Array<string | number | null | undefined>): string {
  return cells.map(csvField).join(',');
}

/** Percentage formatter matching the dashboard's formatRate display. */
export function formatRatePct(rate: number | null | undefined): string {
  return `${((rate ?? 0) * 100).toFixed(1)}%`;
}

/**
 * Duration-in-seconds formatter shared by the "Avg Delivery Time" stat card
 * and the export. Null/undefined (no confirmed samples in the window, or the
 * backend error path) renders an em dash rather than a fake zero — 0.0s would
 * read as "instant delivery".
 */
export function formatSeconds(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined ? '—' : `${seconds.toFixed(1)}s`;
}

/**
 * USD formatter shared by the "Cost per Alert" stat card and the export. Four
 * decimals — unit costs are sub-cent (ADR-041 cites ~$0.003/alert). Missing
 * values and the backend error path (total_cost: -1) render an em dash rather
 * than a fake $0.0000.
 */
export function formatUsd(amount: number | null | undefined): string {
  return amount === null || amount === undefined || amount < 0
    ? '—'
    : `$${amount.toFixed(4)}`;
}

/**
 * Percentage formatter shared by the "Stale Tokens" stat card and the export.
 * Unlike formatRatePct, the input is already 0-100 (not a 0-1 ratio), so it is
 * not scaled. Missing values render 0.0% — matching the card's `?? 0` fallback
 * (the card shows a real zero, not an em dash, on the backend error path where
 * only total_active: -1 is returned).
 */
export function formatStalePct(percentage: number | null | undefined): string {
  return `${(percentage ?? 0).toFixed(1)}%`;
}

/**
 * Human-readable one-liner of the active filters, shared by the CSV header
 * comment and the PDF subtitle. A date range supersedes the time window —
 * mirroring the dashboard behavior where the range overrides the window.
 */
export function describeFilters(filters: DeliveryMetricsExportFilters): string {
  const parts: string[] = [];
  if (filters.startDate || filters.endDate) {
    parts.push(
      `Date range: ${filters.startDate ?? 'beginning'} to ${filters.endDate ?? 'now'}`,
    );
  } else if (filters.timeWindowMinutes !== undefined) {
    parts.push(`Time window: last ${filters.timeWindowMinutes} min`);
  }
  parts.push(`Site: ${filters.siteName ?? 'All sites'}`);
  // "Category" (NotificationLog taxonomy) and "Protocol" (AlertType) are
  // distinct filters — labels match the dashboard's selects.
  parts.push(`Category: ${filters.categoryLabel ?? 'All categories'}`);
  parts.push(`Protocol: ${filters.protocolLabel ?? 'All protocols'}`);
  return parts.join('; ');
}

/** `delivery-metrics-YYYY-MM-DD-HHmm.<ext>` */
export function exportFilename(ext: 'csv' | 'pdf', now: Date = new Date()): string {
  return `delivery-metrics-${dayjs(now).format('YYYY-MM-DD-HHmm')}.${ext}`;
}

interface SummaryStat {
  label: string;
  value: string | number;
}

/** Flatten the headline stat cards into label/value pairs (shared CSV/PDF). */
export function summaryStats(data: DeliveryMetricsResponse): SummaryStat[] {
  const {
    fcm_delivery,
    channel_delivery,
    delivery_latency,
    cost,
    devices,
    manager,
    sms_delivery,
    email_delivery,
    stale_tokens,
  } = data;
  return [
    { label: 'Total Notifications', value: fcm_delivery.total },
    { label: 'FCM Success Rate', value: formatRatePct(fcm_delivery.success_rate) },
    { label: 'FCM Sent', value: fcm_delivery.sent },
    { label: 'FCM Failed', value: fcm_delivery.failed },
    { label: 'FCM Pending', value: fcm_delivery.pending },
    { label: 'Total Deliveries', value: channel_delivery.total_deliveries },
    {
      label: 'Confirmation Rate',
      value: formatRatePct(channel_delivery.confirmation_rate),
    },
    { label: 'Channel Pending', value: channel_delivery.pending },
    { label: 'Active Devices', value: devices.total_devices },
    { label: 'iOS Devices', value: devices.ios },
    { label: 'Android Devices', value: devices.android },
    { label: 'Web Devices', value: devices.web },
    {
      label: 'Delivery Success Rate',
      value: formatRatePct(manager.overall_success_rate),
    },
    { label: 'SMS Success Rate', value: formatRatePct(sms_delivery.success_rate) },
    {
      label: 'Email Success Rate',
      value: formatRatePct(email_delivery.success_rate),
    },
    { label: 'SMS/Email Fallbacks', value: manager.fallback_count ?? 0 },
    { label: 'Fallback Rate', value: formatRatePct(manager.fallback_rate) },
    // Delivery latency over client-confirmed rows only; em dash (not 0.0s)
    // when there are no samples — matches the dashboard card.
    { label: 'Avg Delivery Time', value: formatSeconds(delivery_latency.avg_seconds) },
    { label: 'P95 Delivery Time', value: formatSeconds(delivery_latency.p95_seconds) },
    {
      label: 'Delivery Latency Samples',
      value: Math.max(delivery_latency.sample_count, 0),
    },
    // Stale push-token health (updated_at older than the threshold).
    // stale_percentage arrives 0-100 already (not a 0-1 ratio) and threshold
    // defaults to 30 days — mirrors the "Stale Tokens" card. No em dash: the
    // card renders a real 0 count on the error path (total_active: -1).
    { label: 'Stale Tokens', value: stale_tokens.stale_count ?? 0 },
    {
      label: 'Stale Token Rate',
      value: formatStalePct(stale_tokens.stale_percentage),
    },
    {
      label: 'Stale Token Threshold',
      value: `>${stale_tokens.threshold_days ?? 30}d`,
    },
    // Estimated notification spend (settings-driven unit rates × sent counts).
    // Em dash on the error path (total_cost: -1) — matches the dashboard card.
    { label: 'Cost per Alert', value: formatUsd(cost.cost_per_alert) },
    { label: 'Total Cost', value: formatUsd(cost.total_cost) },
    { label: 'Cost Basis Alerts', value: Math.max(cost.alert_count ?? 0, 0) },
  ];
}

/**
 * Build the multi-section CSV document for the current dashboard snapshot.
 *
 * Layout: leading `#` comment rows (export provenance + active filters),
 * then four sections separated by blank lines — Summary (metric/value),
 * Recent Alert Deliveries, By Provider, and Recent Failure Reasons. Each
 * section is a title row followed by a header row and data rows. Lines are
 * CRLF-terminated per RFC 4180.
 */
export function buildDeliveryMetricsCsv(
  data: DeliveryMetricsResponse,
  filters: DeliveryMetricsExportFilters,
  generatedAt: Date = new Date(),
): string {
  const lines: string[] = [
    '# Aegix Delivery Metrics Export',
    `# Generated: ${dayjs(generatedAt).format('YYYY-MM-DD HH:mm')}`,
    `# Filters: ${describeFilters(filters)}`,
    '',
  ];

  // Section 1 — summary stats
  lines.push('Summary');
  lines.push(csvRow(['Metric', 'Value']));
  for (const stat of summaryStats(data)) {
    lines.push(csvRow([stat.label, stat.value]));
  }
  lines.push('');

  // Section 2 — per-alert delivery summary
  lines.push('Recent Alert Deliveries');
  lines.push(
    csvRow(['Alert ID', 'Total', 'FCM', 'SMS', 'Email', 'Pending', 'Last Activity']),
  );
  for (const alert of data.alert_summary.alerts) {
    lines.push(
      csvRow([
        alert.alert_id,
        alert.total,
        alert.fcm,
        alert.sms,
        alert.email,
        alert.pending,
        alert.latest ?? '',
      ]),
    );
  }
  lines.push('');

  // Section 3 — by provider
  lines.push('By Provider');
  lines.push(csvRow(['Provider', 'Total', 'Sent', 'Failed', 'Success Rate']));
  for (const [name, stat] of Object.entries(data.providers.providers ?? {})) {
    lines.push(
      csvRow([
        name.replace(/_/g, ' '),
        stat.total,
        stat.sent,
        stat.failed,
        formatRatePct(stat.success_rate),
      ]),
    );
  }
  lines.push('');

  // Section 4 — failure reasons
  lines.push('Recent Failure Reasons');
  lines.push(csvRow(['Channel', 'Reason', 'Count']));
  for (const reason of data.failure_reasons.reasons ?? []) {
    lines.push(
      csvRow([reason.channel.toUpperCase(), reason.failure_reason, reason.count]),
    );
  }

  return `${lines.join('\r\n')}\r\n`;
}

/** Trigger a browser download (Blob + anchor idiom, cf. SPDetailsCard). */
export function downloadBlob(
  content: BlobPart,
  filename: string,
  mimeType: string,
): void {
  const url = window.URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// --- PDF -------------------------------------------------------------------

const PAGE_MARGIN_MM = 14;
const PAGE_BOTTOM_MM = 283; // A4 portrait is 297mm tall
const LINE_MM = 4.5;

interface PdfColumn {
  label: string;
  x: number;
}

/**
 * Render the dashboard snapshot to a jsPDF document and trigger its
 * download. jspdf is imported dynamically so it only loads on demand.
 * Plain `text()` layout — readable, not pixel-perfect; no jspdf-autotable
 * dependency.
 */
export async function downloadDeliveryMetricsPdf(
  data: DeliveryMetricsResponse,
  filters: DeliveryMetricsExportFilters,
  generatedAt: Date = new Date(),
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = 18;

  const ensureRoom = (needed: number) => {
    if (y + needed > PAGE_BOTTOM_MM) {
      doc.addPage();
      y = 18;
    }
  };

  const sectionTitle = (title: string) => {
    ensureRoom(14);
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(title, PAGE_MARGIN_MM, y);
    y += 6;
  };

  const tableHeader = (columns: PdfColumn[]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    for (const col of columns) {
      doc.text(col.label, col.x, y);
    }
    y += LINE_MM;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
  };

  const tableRow = (columns: PdfColumn[], cells: Array<string | number>) => {
    ensureRoom(LINE_MM);
    cells.forEach((cell, i) => {
      doc.text(String(cell), columns[i].x, y);
    });
    y += LINE_MM;
  };

  // Header
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Delivery Metrics', PAGE_MARGIN_MM, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110, 110, 110);
  doc.text(
    `Generated: ${dayjs(generatedAt).format('YYYY-MM-DD HH:mm')}`,
    PAGE_MARGIN_MM,
    y,
  );
  y += 5;
  doc.text(`Filters: ${describeFilters(filters)}`, PAGE_MARGIN_MM, y);
  doc.setTextColor(0, 0, 0);

  // Summary — two columns of label/value lines
  sectionTitle('Summary');
  doc.setFontSize(9);
  const stats = summaryStats(data);
  stats.forEach((stat, i) => {
    const isLeftColumn = i % 2 === 0;
    if (isLeftColumn) {
      ensureRoom(LINE_MM);
    }
    const x = isLeftColumn ? PAGE_MARGIN_MM : 110;
    doc.text(`${stat.label}: ${stat.value}`, x, y);
    if (!isLeftColumn || i === stats.length - 1) {
      y += LINE_MM;
    }
  });

  // Recent Alert Deliveries
  const alertColumns: PdfColumn[] = [
    { label: 'Alert ID', x: PAGE_MARGIN_MM },
    { label: 'Total', x: 44 },
    { label: 'FCM', x: 64 },
    { label: 'SMS', x: 84 },
    { label: 'Email', x: 104 },
    { label: 'Pending', x: 124 },
    { label: 'Last Activity', x: 148 },
  ];
  sectionTitle('Recent Alert Deliveries');
  tableHeader(alertColumns);
  if (data.alert_summary.alerts.length === 0) {
    tableRow([alertColumns[0]], ['No alert delivery data available.']);
  }
  for (const alert of data.alert_summary.alerts) {
    tableRow(alertColumns, [
      `#${alert.alert_id}`,
      alert.total,
      alert.fcm,
      alert.sms,
      alert.email,
      alert.pending,
      alert.latest ? dayjs(alert.latest).format('MMM DD, HH:mm') : '-',
    ]);
  }

  // By Provider
  const providerColumns: PdfColumn[] = [
    { label: 'Provider', x: PAGE_MARGIN_MM },
    { label: 'Total', x: 90 },
    { label: 'Sent', x: 115 },
    { label: 'Failed', x: 140 },
    { label: 'Rate', x: 165 },
  ];
  sectionTitle('By Provider');
  tableHeader(providerColumns);
  const providerEntries = Object.entries(data.providers.providers ?? {});
  if (providerEntries.length === 0) {
    tableRow([providerColumns[0]], ['No provider data available.']);
  }
  for (const [name, stat] of providerEntries) {
    tableRow(providerColumns, [
      name.replace(/_/g, ' '),
      stat.total,
      stat.sent,
      stat.failed,
      formatRatePct(stat.success_rate),
    ]);
  }

  // Recent Failure Reasons — reasons can be long, so wrap them.
  const failureColumns: PdfColumn[] = [
    { label: 'Channel', x: PAGE_MARGIN_MM },
    { label: 'Reason', x: 44 },
    { label: 'Count', x: 175 },
  ];
  sectionTitle('Recent Failure Reasons');
  tableHeader(failureColumns);
  const failureReasons = data.failure_reasons.reasons ?? [];
  if (failureReasons.length === 0) {
    tableRow([failureColumns[0]], ['No failures in this window.']);
  }
  for (const reason of failureReasons) {
    const wrapped = doc.splitTextToSize(reason.failure_reason, 125) as string[];
    ensureRoom(wrapped.length * LINE_MM);
    doc.text(reason.channel.toUpperCase(), failureColumns[0].x, y);
    doc.text(wrapped, failureColumns[1].x, y);
    doc.text(String(reason.count), failureColumns[2].x, y);
    y += wrapped.length * LINE_MM;
  }

  doc.save(exportFilename('pdf', generatedAt));
}
